import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import axios from 'axios';
import { Shield, Save, RefreshCw, Info, AlertCircle } from 'lucide-react';

const DataGovernanceManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [entities, setEntities] = useState([]);
  const [policies, setPolicies] = useState({});
  const [changes, setChanges] = useState({});

  // Get current tenant code
  const getTenantCode = () => {
    // Try from user's selected tenant first
    if (user?.selectedTenant?.code) {
      console.log('🏢 DataGovernance: Using selectedTenant.code:', user.selectedTenant.code);
      return user.selectedTenant.code;
    }
    // Try from localStorage
    const savedCode = localStorage.getItem('selectedTenantCode');
    if (savedCode) {
      console.log('🏢 DataGovernance: Using localStorage selectedTenantCode:', savedCode);
      return savedCode;
    }
    // Fallback to user's default tenant
    if (user?.defaultTenant?.code) {
      console.log('🏢 DataGovernance: Using defaultTenant.code:', user.defaultTenant.code);
      return user.defaultTenant.code;
    }
    // Last resort: user's tenantCode
    console.log('🏢 DataGovernance: Using user.tenantCode:', user?.tenantCode);
    return user?.tenantCode;
  };

  // Get axios config with tenant code header
  const getAxiosConfig = () => {
    const tenantCode = getTenantCode();
    console.log('🏢 DataGovernance: Tenant code for request:', tenantCode);
    console.log('🏢 DataGovernance: User object:', {
      selectedTenant: user?.selectedTenant,
      defaultTenant: user?.defaultTenant,
      tenantCode: user?.tenantCode
    });
    return {
      headers: {
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      }
    };
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load available entities from entity configs (via data-governance endpoint which reads from configs)
      let entitiesList = [];
      try {
        const entitiesResponse = await axios.get('/api/data-governance/entities', getAxiosConfig());
        entitiesList = entitiesResponse.data.entities || [];
      } catch (err) {
        console.error('Error loading entities from config:', err);
        // Fallback to Customer only if entity config endpoint fails
        entitiesList = [
          { type: 'Customer', displayName: 'Customer', description: 'Customer records' }
        ];
        setError('Failed to load entities from config. Showing Customer only. Please refresh the page.');
      }

      // Load existing policies
      const policiesResponse = await axios.get('/api/data-governance/policies', getAxiosConfig());
      const policiesList = policiesResponse.data.policies || [];

      // Create a map of policies by entity type
      const policiesMap = {};
      policiesList.forEach(policy => {
        policiesMap[policy.EntityType] = policy;
      });

      // Merge entities with their policies
      const entitiesWithPolicies = entitiesList.map(entity => {
        const policy = policiesMap[entity.type] || {
          PolicyType: 3, // Default to Policy 3 (Full-Access)
          OwnershipField: entity.type === 'Customer' || entity.type === 'Lead' || entity.type === 'Opportunity' 
            ? 'SalespersonID' 
            : 'CreatedBy',
          NotifyOwnerOnEdit: true
        };

        return {
          ...entity,
          policyId: policy.PolicyID || null,
          policyType: policy.PolicyType,
          ownershipField: policy.OwnershipField,
          notifyOwnerOnEdit: policy.NotifyOwnerOnEdit
        };
      });

      setEntities(entitiesWithPolicies);
      setPolicies(policiesMap);
      setChanges({});
    } catch (err) {
      console.error('Error loading data governance:', err);
      setError(err.response?.data?.message || 'Failed to load data governance policies');
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyChange = (entityType, field, value) => {
    const entity = entities.find(e => e.type === entityType);
    if (!entity) return;

    const changeKey = `${entityType}_${field}`;
    const newChanges = { ...changes };
    
    if (!newChanges[entityType]) {
      newChanges[entityType] = {};
    }

    newChanges[entityType][field] = value;
    setChanges(newChanges);

    // Update local state for immediate UI feedback
    setEntities(prevEntities =>
      prevEntities.map(e =>
        e.type === entityType
          ? { ...e, [field]: value }
          : e
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      // Save each changed policy
      const savePromises = Object.entries(changes).map(async ([entityType, changes]) => {
        const entity = entities.find(e => e.type === entityType);
        if (!entity) return;

        const payload = {
          entityType,
          policyType: changes.policyType !== undefined ? changes.policyType : entity.policyType,
          ownershipField: changes.ownershipField !== undefined ? changes.ownershipField : entity.ownershipField,
          notifyOwnerOnEdit: changes.notifyOwnerOnEdit !== undefined 
            ? changes.notifyOwnerOnEdit 
            : (entity.notifyOwnerOnEdit !== false)
        };

        return axios.post('/api/data-governance/policies', payload, getAxiosConfig());
      });

      await Promise.all(savePromises);

      setSuccessMessage('Policies saved successfully');
      setChanges({});
      
      // Reload data to get updated policies
      setTimeout(() => {
        loadData();
      }, 1000);
    } catch (err) {
      console.error('Error saving policies:', err);
      console.error('Error response:', err.response);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        headers: err.response?.headers
      });
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save policies';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getPolicyDescription = (policyType) => {
    switch (policyType) {
      case 1:
        return 'Owner-Manager: Users view/edit own data; Managers view/edit all (owner gets notification on edit)';
      case 2:
        return 'Owner-View-Others: Users view/edit own data; can view but not edit others';
      case 3:
        return 'Full-Access: Everyone can view and edit all data';
      default:
        return '';
    }
  };

  const getPolicyColor = (policyType) => {
    switch (policyType) {
      case 1:
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 2:
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 3:
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const hasChanges = Object.keys(changes).length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Data Governance Management</h1>
        </div>
        <p className="text-gray-600 mt-2">
          Configure data access policies for each entity type. Policies control who can view and edit data.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <Info className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {entities.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-yellow-900 mb-2">No Entity Entitlements</h3>
          <p className="text-yellow-700 mb-4">
            Your tenant has not been assigned any entity entitlements. Please contact your Super Admin to request entity access.
          </p>
          <p className="text-sm text-yellow-600">
            Once entities are assigned to your tenant, you will be able to configure data governance policies for them.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Policy Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ownership Field
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notify Owner on Edit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entities.map((entity) => {
                  const isChanged = changes[entity.type];
                  return (
                    <tr key={entity.type} className={isChanged ? 'bg-yellow-50' : ''}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{entity.displayName}</div>
                        <div className="text-sm text-gray-500 max-w-xs break-words">{entity.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={entity.policyType}
                          onChange={(e) => handlePolicyChange(entity.type, 'policyType', parseInt(e.target.value))}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value={1}>Policy 1: Owner-Manager</option>
                          <option value={2}>Policy 2: Owner-View-Others</option>
                          <option value={3}>Policy 3: Full-Access</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <select
                          value={entity.ownershipField}
                          onChange={(e) => handlePolicyChange(entity.type, 'ownershipField', e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value="CreatedBy">CreatedBy</option>
                          <option value="SalespersonID">SalespersonID</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={entity.notifyOwnerOnEdit !== false}
                          onChange={(e) => handlePolicyChange(entity.type, 'notifyOwnerOnEdit', e.target.checked)}
                          disabled={entity.policyType !== 1}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        {entity.policyType !== 1 && (
                          <span className="ml-2 text-xs text-gray-400">(Only for Policy 1)</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPolicyColor(entity.policyType)}`}>
                          {getPolicyDescription(entity.policyType)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          {hasChanges && (
            <span className="text-yellow-600 font-medium">You have unsaved changes</span>
          )}
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadData}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4 inline mr-2" />
            Refresh
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 inline mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 inline mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Policy Types Explained</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>
            <strong>Policy 1 (Owner-Manager):</strong> Users can view and edit their own data. Managers can view and edit all data. When a manager edits an owner's record, the owner receives a notification.
          </li>
          <li>
            <strong>Policy 2 (Owner-View-Others):</strong> Users can view and edit their own data. Users can view others' data but cannot edit it.
          </li>
          <li>
            <strong>Policy 3 (Full-Access):</strong> Everyone can view and edit all data. This is the default policy if no policy is configured.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DataGovernanceManager;

